/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FormField,
  type FormFieldProps,
  type FormFieldSize,
  type MultiSelectProps,
  Select,
  type SelectOption,
  type SelectSize,
  type SingleSelectProps,
} from "@aurem/ui";
import { useCallback } from "react";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import { get } from "../../utils";

type SelectOmitProps = "label" | "value" | "onChange";

type BaseSelectControlProps = Partial<Omit<FormFieldProps, "size">> & {
  name: string;
  formElementClass?: string;
  clear?: boolean;
};

export type SingleSelectControlProps = Omit<
  SingleSelectProps,
  SelectOmitProps
> &
  BaseSelectControlProps & {
    onChange?: (value: string | undefined, item?: SelectOption) => void;
  };

export type MultiSelectControlProps = Omit<MultiSelectProps, SelectOmitProps> &
  BaseSelectControlProps & {
    onChange?: (value: string[], item?: SelectOption) => void;
  };

type Props = SingleSelectControlProps | MultiSelectControlProps;

export const SelectControl = (props: Props) => {
  const {
    label,
    message,
    required,
    tooltipContent,
    accessory,
    formElementClass,
    name,
    invalid,
    onChange,
    clear,
    description,
    size = "md",
    ...restSelectProps
  } = props;
  const { control } = useFormContext();
  const { errors } = useFormState();
  const errorMessage = get(errors, name)?.message as string | undefined;

  const formFieldSize: Record<SelectSize, FormFieldSize> = {
    sm: "sm",
    md: "md",
    lg: "lg",
  };

  const handleChange = useCallback(
    (onFieldChange: (...event: any[]) => void) => {
      return (value: string | string[] | undefined, item?: SelectOption) => {
        if (onChange) {
          onChange(value as never, item);
        }
        onFieldChange(value);
      };
    },
    [onChange],
  );

  const selectElement = (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: onFieldChange, value } }) => {
        const handleClear = () => {
          const clearValue = restSelectProps.multiple ? [] : undefined;
          onFieldChange(clearValue);
          if (onChange) {
            onChange(clearValue as never);
          }
        };

        return (
          <Select
            {...restSelectProps}
            onChange={handleChange(onFieldChange)}
            value={value}
            invalid={invalid || !!errorMessage}
            size={size}
            onClear={
              restSelectProps.onClear || (clear ? handleClear : undefined)
            }
          />
        );
      }}
    />
  );

  return (
    <FormField
      label={label}
      message={message ?? errorMessage}
      description={description}
      required={required}
      tooltipContent={tooltipContent}
      className={formElementClass}
      accessory={accessory}
      size={formFieldSize[size]}
    >
      {selectElement}
    </FormField>
  );
};
